import { defineStore } from 'pinia'
import { AccountInfo } from "@azure/msal-browser";
import axios, { AxiosRequestConfig } from 'axios';

import { useStorage, StorageSerializers } from "@vueuse/core"

import { EnvService } from '@/services/env';

export interface  Person {
  department : string
  name : string
  preferred_name : string
  title : string
  uvid : string
}

const useSessionStore = defineStore('SessionStore', {
    state : () => {
      return {
        app_name : '',
        app_title : '',
        backend_path : '',
        environment : '',
        dynamic_roles : [] as string[],

        accessToken : '',
        //accessTokenClaims : {},
        account : {} as AccountInfo | undefined,
        displayName : '',
        uvid : '',
        selected_username : '',
        usernames  : [] as string[], // used when multiple accounts are detected

        session_issues : false,
        error_messages : [] as string[],

        impersonated_account : useStorage(`${process.env.VUE_APP_NAME}.impersonated_account`, {}, localStorage, { serializer: StorageSerializers.object }),
      }
    },
    getters : {
      getImpersonation(state){
        return state.impersonated_account
      },
      isImpersonationSet(state){
        if(!state.impersonated_account) return false
        return Object.keys(state.impersonated_account).length > 0
      },
      DisplayName(state){
        if(state.impersonated_account && Object.keys(state.impersonated_account).length > 0){
          const impersonated_account = state.impersonated_account
          return impersonated_account.preferred_name
        }
        return state.account?.name
      },
      invalidAccessToken(state){
          if (state.accessToken === '') return true
          try {
              const claims = JSON.parse(atob(state.accessToken.split('.')[1]))
              return claims.exp <= Date.now()/1000
          } catch (err) {
              console.log("Error Validating Token/Claims: ", err)
          }
          return true
      },
      isAdmin(state){
          const roles = state.account?.idTokenClaims?.roles ?? []
          return roles.includes((new EnvService()).getAttribute('VUE_APP_ADMIN_ROLE'))
      },
      roles(state){
          return state.account?.idTokenClaims?.roles ?? [];
      },
      auth_header(state){
          return {
              headers: {
                  Authorization : `Bearer ${state.accessToken}`
              } 
            }
            
      }
    },
    actions : {
      inRole(role : string){
        return this.dynamic_roles.includes(role) || this.roles.includes(role);
      },

      async setImpersonation(person : Person | any){
        this.impersonated_account = person
      },

      getImpersonationUVID(){

        // if not impersonating, return logged in user/uvid
        const impersonated_account = this.impersonated_account
        return (impersonated_account?.uvid) ? impersonated_account.uvid : this.uvid
      },

        async updateUserAccount(account : AccountInfo | undefined){
          this.account = account;
        },
        async updateDisplayName(displayName : string){
          this.displayName = displayName;
        },
        async updateUserName(username : string){
          this.uvid = username;
          this.selected_username = username;
        },
        async updateAccountUserNames(usernames : string[]){
          this.usernames = usernames;
        },
        updateAccessToken(accessToken : string){
          this.accessToken = accessToken;
        },
        addErrorMessage(message : string){
          if(!this.error_messages.includes(message)) {
            this.error_messages.push(message)
          }
        },

        async request_data(method : string, url : string, func_name : string, data : {} | undefined){

          // fun with typescript - since you can't have dynamic properties of a dictionary
          interface ParamsObject {
            [key: string]: any
          }

          const params : ParamsObject = {}

          if(this.isImpersonationSet) {
            params['impersonate'] = this.impersonated_account.uvid
          }

          // since we are [possibly]appending an impersonation param, we need to make sure any pther params are handled correctly
          const [url_path, url_params] = url.split('?');

          if(url_params){
            for(const param of url_params.split('&')){
              const [key,val] = param.split('=')
              params[key] = val
            }
          }

          const config : AxiosRequestConfig = {
            method : method,
            url : url,
            params : params,
            data : data,
            headers : { 
              Authorization : `Bearer ${this.accessToken}`,
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            responseType : 'json'
          }
          
          return await axios(`${this.backend_path}${url_path}`, config)
            .then(response => {
              if(response.data.status == 'error'){
                if(response.data.statusMessage && response.data.statusMessage.length > 0){
                  throw new Error(response.data.statusMessage)
                } else {
                  throw new Error('Unhandled Error')
                }
              }
              return response.data;
            })
            .then(data => {
              if('status' in data && data.status == 'failure'){
                return data
              }
              return {...data.data}
            })
            .catch((e) => {
              let message = ""
              
              if(typeof e === 'string'){
                this.addErrorMessage(`${func_name} : ${e.substring(0, 200)}`)
                return []
              }

              if(typeof e === 'object' && 'message' in e && !('data' in e.response)){
                this.addErrorMessage(`${func_name} : ${e.message.substring(0, 200)}`)
                return []
              }
              
              const response = e.response.data
              if (typeof response === 'string'){
                message = response
              }
              
              if(typeof response === 'object' && response !== null){
                if('detail' in response){
                  message = response.detail
                } else if('message' in response){
                  message = response.message
                } else if ('metadata' in response && 'statusMessage' in response.metadata) {
                  message = response.metadata.statusMessage
                } else {
                  message = "Unknown error"
                }
              }
              this.addErrorMessage(`${func_name} : ${message.substring(0, 200)}`)
              return [] // always need to return something
            })
        }

    },
    
  })
  
  export { useSessionStore }
